/*
 * The operations surface: what an administrator looks at when something is wrong — is the instance
 * running, is its work being done, is there room left. The section is named by the Administration
 * frame it hangs in ("admin.nav.operations"), so there is no title of its own; `ops.lead` is the
 * sentence that frame prints under the heading.
 *
 * The names on the left of the list are copy; what stands on the left of a job's or a module's row is
 * **data** — the job's own name, the module's id — and is not translated, because an operator who has
 * to find that name in a log is looking for the spelling the log uses.
 */

export const en = {
	"ops.lead": "What this instance is doing, re-read every thirty seconds.",
	"ops.loading": "Reading the instance …",

	// ---- the verdict ----
	// The one line the page is opened for: is anything broken. It is composed of the clauses below,
	// trouble first — that is what the reader came for — and the evidence that the scheduler is alive
	// after it, either way.
	"ops.verdict.ok": "Everything healthy",
	"ops.verdict.noFailures": "no failures",
	"ops.verdict.ran": "{count} jobs ran in the last hour",
	"ops.verdict.ran.one": "one job ran in the last hour",
	"ops.verdict.ranNone": "no job has run in the last hour",
	"ops.verdict.jobFailing": "{name} has failed {count} times in a row",
	"ops.verdict.jobFailing.one": "{name} failed the last time it ran",
	"ops.verdict.jobsFailing": "{count} jobs are failing, {name} among them",
	// The singular of the line above. It is not reached today — one failing job is named by
	// `jobFailing`, which says what it owes — and it stands here because a counted noun has a
	// singular whatever the caller happens to pass (`core/i18n/glossary.test.ts`).
	"ops.verdict.jobsFailing.one": "one job is failing, {name}",
	"ops.verdict.module": "{module} is not healthy",
	"ops.verdict.backup": "the last backup did not finish",

	// ---- is it running ----
	"ops.instance": "This instance",
	"ops.version": "Version",
	"ops.built": "Built",
	"ops.started": "Started",
	"ops.uptime": "Uptime",
	"ops.uptime.days": "{days} d {hours} h",
	"ops.uptime.hours": "{hours} h {minutes} min",
	"ops.uptime.minutes": "{minutes} min",
	"ops.uptime.seconds": "{seconds} s",
	// Who is on the instance at this moment. Two figures and not one: a colleague with two tabs open
	// is two connections, so "3 connections from 2 people" says what neither number says alone. The
	// people clause is left off while nobody is past the handshake yet — the one moment the hub holds
	// a connection with no person behind it.
	"ops.sockets": "Connected",
	"ops.sockets.connections": "{count} connections",
	"ops.sockets.connections.one": "one connection",
	"ops.sockets.people": "{count} people",
	"ops.sockets.people.one": "one person",
	"ops.sockets.value": "{connections} from {people}",
	"ops.memory": "Memory",
	"ops.memory.limit": "{used} of {limit}",
	"ops.module.healthy": "Healthy",
	"ops.module.unhealthy": "Not healthy",
	"ops.module.disabled": "Disabled",
	// A module's name for a reader, keyed by its id. A module with no entry here keeps its id, so a
	// module that arrives after this build appears rather than disappearing.
	"ops.module.name.chat": "Chat",
	"ops.module.name.core": "Core",

	// ---- is the work being done ----
	"ops.work": "Work",
	// No heading of its own for the list: the row it folds under names it and counts it.
	"ops.jobs.none": "No job is registered.",
	// A job that is doing its work says one thing; a job that is failing says what it owes first,
	// because that is the line an operator is looking for.
	"ops.job.lastOk": "Last ok {when}",
	"ops.job.never": "Not run yet",
	"ops.job.failing": "{count} failures in a row · {last}",
	"ops.job.failing.one": "{count} failure · {last}",
	// How many jobs there are, on the row the list folds under.
	"ops.jobs.all": "All {count} jobs",
	"ops.jobs.all.one": "The one job",
	// What each job is for, keyed by the name the scheduler registered it under and shown on that
	// name. The names are data and are never translated; these sentences are what tells an operator
	// whether a job failing at four in the morning is worth getting up for. A job with no sentence
	// here shows its name alone.
	"ops.job.what.chat.draft.prune": "Retires the tombstones of deleted messages thirty days on.",
	"ops.job.what.chat.emoji.prune": "Bounds the table of recently used emoji.",
	"ops.job.what.chat.render.backfill": "Renders messages again after the renderer has changed.",
	"ops.job.what.chat.tombstone.erase": "Erases the text behind a deleted message, nightly.",
	"ops.job.what.core.admission.sweep":
		"Forgets the rate-limit windows of visitors who have gone away.",
	"ops.job.what.core.auth.sweep":
		"Clears out expired sessions, device trusts, logins and one-time secrets.",
	"ops.job.what.core.backup.prune": "Drops backup runs from the journal after two years.",
	"ops.job.what.core.changelog.prune": "Drops change-log rows older than thirty days.",
	"ops.job.what.core.db.maintain":
		"Refreshes the database statistics and gives freed space back to the disk.",
	"ops.job.what.core.files.drafts": "Removes uploads that were never sent with a message.",
	"ops.job.what.core.files.gc": "Deletes attachment bytes nothing points at any more.",
	"ops.job.what.core.files.incoming": "Clears out uploads that were begun and never finished.",
	"ops.job.what.core.files.variants": "Makes the thumbnails and preview sizes of new pictures.",
	"ops.job.what.core.mail.drain": "Sends the mail waiting in the outbox.",
	"ops.job.what.core.mail.prune": "Deletes the records of mail settled more than three months ago.",
	"ops.job.what.core.notify.drain": "Sends the notifications waiting in the outbox.",
	"ops.job.what.core.notify.prune": "Deletes settled notification rows older than thirty days.",
	"ops.job.what.core.search.integrity": "Checks the search index against the messages, weekly.",
	"ops.outbox.mail": "Mail waiting",
	"ops.outbox.notification": "Notifications waiting",

	// ---- is there room ----
	"ops.storage": "Storage",
	"ops.db": "Database",
	"ops.db.value": "{total} · {wal} in the write-ahead log",
	"ops.blobs": "Attachments",
	"ops.blobs.quota": "{used} of {quota}",
	"ops.disk": "Disk",
	"ops.disk.value": "{free} free of {total}",
	// A volume whose size the process cannot read still knows how much of it is left.
	"ops.disk.free": "{free} free",
	"ops.backup": "Last backup",
	"ops.backup.none": "No backup has finished yet.",
	"ops.backup.ok": "{size} written",
	"ops.backup.failed": "Did not finish,",
} as const satisfies Record<string, string>
